<template>
  <div
    class="product-grades"
    :class="{ [$class('loading')]: loading }"
  >
    <p
      class="text-heading-s text-heading-m-desktop"
      v-text="$string('product.grades.title')"
    />

    <splide
      class="product-grades__carousel"
      :has-track="false"
      :options="carouselConfig"
      @splide:moved="handleSplideMoved"
    >
      <splide-track>
        <splide-slide
          v-for="grade of filteredGrades"
          :key="grade.handle"
          class="product-grades__slide"
        >
          <a
            class="product-grades__card"
            :href="`/products/${grade?.handle}`"
          >
            <div class="product-grades__card--header">
              <p
                class="text-heading-2xl text-heading-3xl-desktop"
                v-text="grade?.title"
              />

              <product-prices
                class="product-grades__card--prices"
                :available="grade?.available"
                :loading="!grade"
                :price="grade?.price?.amount"
                :varies="grade?.price?.varies"
                :price-first="false"
              />
            </div>

            <p
              v-if="grade?.additionalDescription"
              class="text-body-s-regular product-grades__card--text"
              v-text="grade?.additionalDescription"
            />

            <taste-notes
              v-if="grade?.tasteNotes.length"
              class="product-grades__card--taste-notes"
              :notes="getFilteredTasteNotes(grade)"
            />
          </a>
        </splide-slide>
      </splide-track>

      <div class="product-grades__arrows splide__arrows">
        <button
          class="splide__arrow splide__arrow--prev"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button
          class="splide__arrow splide__arrow--next"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>

      <ul
        v-if="filteredGrades.length"
        class="product-grades__custom-pagination"
      >
        <li
          class="text-body-s-regular"
          v-text="`${currentIndex + 1}/`"
        />
        <li
          class="text-body-s-regular"
          v-text="filteredGrades.length"
        />
      </ul>
    </splide>
  </div>
</template>

<script>

/**
 * Vue: Product grades (product-grades)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Array} [tasteNotesKey] - Key list to show.
 * @param {Boolean|Object} product - Canvas formatted product object.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import ProductPrices from '~global/product-prices/product-prices'
import TasteNotes from '~async/taste-notes/taste-notes'

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

export default {
  name: 'ProductGrades',

  components: {
    Splide,
    SplideSlide,
    SplideTrack,
    ProductPrices,
    TasteNotes,
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    tasteNotesKey: {
      type: Array,
      default: () => ['color', 'texture', 'intensity'],
    },
  },

  data() {
    return {
      currentIndex: 0,
    }
  },

  computed: {

    /**
     * Calculate Splide config object.
     * - Disables dragging if there are not enough cards.
     * - Arrows and pagination are disabled with CSS in the main Splide
     *   stylesheet.
     * @returns {Object} splide config.
     */
    carouselConfig() {
      return {
        arrows: true,
        drag: true,
        gap: 'var(--spacing-l)',
        mediaQuery: 'min',
        pagination: false,
        type: 'slide',
        omitEnd: false,
        perPage: 1,
      }
    },

    /**
     * Returns filtered grades.
     * @returns {Array}
     */
    filteredGrades() {
      return this.product?.grades.filter(grade => grade.handle !== this.product?.handle)
    },
  },

  methods: {

    /**
     * Handle Splide moved event.
     * - Updates current slide index.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     */
    handleSplideMoved(_, newIndex) {
      this.currentIndex = newIndex
    },

    /**
     * Returns filtered taste notes.
     * @param {Object} grade - Grade data.
     * @returns {Array}
     */
    getFilteredTasteNotes(grade) {
      return grade?.tasteNotes.filter(notes => this.tasteNotesKey.includes(notes.key))
    },
  },
}
</script>

<style lang="scss">
@import './product-grades';
</style>
